// Sürücü Puantajı — salt-okunur performans API hook'u.
//
//   - getPerformance(asOf)          → GET /driver-scheduler/performance?asOf=YYYY-MM-DD
//   - getDriverDetail(driverId, month) → GET /driver-scheduler/performance/:driverId?month=YYYY-MM

import { useCallback } from "react";

import { useHttpClient } from "shared/hooks/http-hook";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/driver-scheduler`;

export const useDriverPerformanceApi = () => {
  const { isLoading, error, sendRequest } = useHttpClient();

  const getPerformance = useCallback(
    async (asOf) => {
      const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
      return sendRequest(`${BASE}/performance${qs}`);
    },
    [sendRequest],
  );

  const getDriverDetail = useCallback(
    async (driverId, { start, end } = {}) => {
      if (!driverId) return null;
      const params = [];
      if (start) params.push(`start=${encodeURIComponent(start)}`);
      if (end) params.push(`end=${encodeURIComponent(end)}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      return sendRequest(`${BASE}/performance/${driverId}${qs}`);
    },
    [sendRequest],
  );

  const getRestFleet = useCallback(
    async (asOf) => {
      const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
      return sendRequest(`${BASE}/performance/rest${qs}`);
    },
    [sendRequest],
  );

  const getRestDetail = useCallback(
    async (driverId, { start, end } = {}) => {
      if (!driverId) return null;
      const params = [];
      if (start) params.push(`start=${encodeURIComponent(start)}`);
      if (end) params.push(`end=${encodeURIComponent(end)}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      return sendRequest(`${BASE}/performance/rest/${driverId}${qs}`);
    },
    [sendRequest],
  );

  // ---------- Puantaj Kayıtları (worklog fact sheet) ----------

  const listWorklogs = useCallback(
    async ({ start, end, driverId, includeRemoved } = {}) => {
      const params = [];
      if (start) params.push(`start=${encodeURIComponent(start)}`);
      if (end) params.push(`end=${encodeURIComponent(end)}`);
      if (driverId) params.push(`driverId=${encodeURIComponent(driverId)}`);
      if (includeRemoved) params.push("includeRemoved=true");
      const qs = params.length ? `?${params.join("&")}` : "";
      return sendRequest(`${BASE}/worklog${qs}`);
    },
    [sendRequest],
  );

  const createWorklog = useCallback(
    async (body) => sendRequest(`${BASE}/worklog`, "POST", JSON.stringify(body)),
    [sendRequest],
  );

  const updateWorklog = useCallback(
    async (id, patch) => sendRequest(`${BASE}/worklog/${id}`, "PATCH", JSON.stringify(patch)),
    [sendRequest],
  );

  const removeWorklog = useCallback(
    async (id) => sendRequest(`${BASE}/worklog/${id}`, "DELETE"),
    [sendRequest],
  );

  const restoreWorklog = useCallback(
    async (id) => sendRequest(`${BASE}/worklog/${id}/restore`, "POST"),
    [sendRequest],
  );

  const runWorklogSnapshot = useCallback(
    async (date, force = false) => sendRequest(`${BASE}/worklog/snapshot`, "POST", JSON.stringify({ date, force })),
    [sendRequest],
  );

  return {
    isLoading,
    error,
    getPerformance,
    getDriverDetail,
    getRestFleet,
    getRestDetail,
    listWorklogs,
    createWorklog,
    updateWorklog,
    removeWorklog,
    restoreWorklog,
    runWorklogSnapshot,
  };
};

export default useDriverPerformanceApi;
